import React from 'react';
import { Dialog, DialogTrigger, TextField, PasswordField, Text, Flex, Box, Button, Select, Checkbox, TagBadge, CollapsibleSection } from '@chatsdet/ui';
import { Cube, Lock, Gear, Globe } from '@phosphor-icons/react';
import { type EnvironmentTemplate } from '../../../hooks/useEnvironmentTemplates';
import { useCreateInstance } from '../../../hooks/useEnvironmentInstances';
import { useCustomDomains } from '../../../hooks/useCustomDomains';

type CreateInstanceDialogProps = {
  template: EnvironmentTemplate;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
};

const EXPIRATION_OPTIONS = [
  { id: '1', label: '1 hour' },
  { id: '2', label: '2 hours' },
  { id: '4', label: '4 hours' },
  { id: '8', label: '8 hours' },
  { id: '12', label: '12 hours' },
  { id: '24', label: '24 hours' },
];

function generateDefaultName(templateName: string): string {
  const timestamp = new Date().toISOString().slice(0, 16).replace('T', '-').replace(':', '');
  return `${templateName.toLowerCase().replace(/\s+/g, '-')}-${timestamp}`;
}

/** Mirrors the server's validateSandboxSlug shape rules (reserved words are server-side). */
const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const PLATFORM_DOMAIN_KEY = 'platform';

function validateSlugShape(slug: string): string | null {
  if (slug.length > 30) return 'Must be 30 characters or less';
  if (!SLUG_PATTERN.test(slug) || slug.includes('--')) {
    return 'Lowercase letters, digits, and single hyphens only';
  }
  return null;
}

export function CreateInstanceDialog({
  template,
  isOpen,
  onOpenChange,
  onSuccess,
}: CreateInstanceDialogProps) {
  const createMutation = useCreateInstance();
  const customDomainsQuery = useCustomDomains({ enabled: isOpen });

  const [name, setName] = React.useState('');
  const [expiresInHours, setExpiresInHours] = React.useState('1');
  const [isPrivate, setIsPrivate] = React.useState(false);
  // Default ON: new sandboxes provision an interactive agent unless the user
  // opts out. The verification/test-run path stays agentless server-side.
  const [withAgent, setWithAgent] = React.useState(true);
  const [useDefaults, setUseDefaults] = React.useState(true);
  const [userConfig, setUserConfig] = React.useState<Record<string, string>>({});
  const [errors, setErrors] = React.useState<Partial<Record<string, string>>>({});
  const [customSlug, setCustomSlug] = React.useState('');
  const [domainKey, setDomainKey] = React.useState(PLATFORM_DOMAIN_KEY);

  const hasConfigSchema = template.configSchema && template.configSchema.length > 0;

  const domainsData = customDomainsQuery.data?.data;
  const attachedDomains = (domainsData?.domains ?? []).filter((d) => d.status === 'attached');
  const customUrlAvailable =
    (Boolean(domainsData?.envDomain) || attachedDomains.length > 0) && !isPrivate;
  const domainOptions = [
    ...(domainsData?.envDomain
      ? [{ id: PLATFORM_DOMAIN_KEY, label: domainsData.envDomain }]
      : []),
    ...attachedDomains.map((d) => ({ id: d.id, label: d.domain })),
  ];
  // The platform domain can be absent (envDomain unset) — fall back to the
  // first option so the select never points at a missing entry.
  const effectiveDomainKey = domainOptions.some((o) => o.id === domainKey)
    ? domainKey
    : (domainOptions[0]?.id ?? PLATFORM_DOMAIN_KEY);
  const selectedDomain = attachedDomains.find((d) => d.id === effectiveDomainKey);
  const hostnamePreview = customSlug.trim()
    ? selectedDomain
      ? `${customSlug.trim()}.${selectedDomain.domain}`
      : domainsData?.envDomain
        ? `${domainsData.orgSlug}-${customSlug.trim()}.${domainsData.envDomain}`
        : null
    : null;

  // Initialize form when dialog opens
  React.useEffect(() => {
    if (isOpen) {
      setName(generateDefaultName(template.name));
      setExpiresInHours('1');
      setIsPrivate(false);
      // Default ON: provision an agent unless the user opts out.
      setWithAgent(true);
      setUseDefaults(true);
      // Initialize config fields from schema with defaults from template
      const initialConfig: Record<string, string> = {};
      template.configSchema?.forEach((field) => {
        // Pre-fill with template's configDefaults if available
        initialConfig[field.key] = template.configDefaults?.[field.key] ?? '';
      });
      setUserConfig(initialConfig);
      setErrors({});
      setCustomSlug('');
      setDomainKey(PLATFORM_DOMAIN_KEY);
    }
  }, [isOpen, template]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<string, string>> = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.length > 100) {
      newErrors.name = 'Name must be 100 characters or less';
    }

    // Only validate required config fields when not using defaults
    if (!useDefaults) {
      template.configSchema?.forEach((field) => {
        if (field.required && !userConfig[field.key]?.trim()) {
          newErrors[`config_${field.key}`] = `${field.key} is required`;
        }
      });
    }

    if (customSlug.trim()) {
      const slugError = validateSlugShape(customSlug.trim());
      if (slugError) {
        newErrors.customSlug = slugError;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submit = async () => {
    if (createMutation.isPending) return;
    if (!validate()) return;

    try {
      const slug = !isPrivate && customSlug.trim() ? customSlug.trim() : undefined;
      await createMutation.mutateAsync({
        templateId: template.id,
        name: name.trim(),
        // Send empty config when using defaults, otherwise send user-specified values
        userConfig: useDefaults ? {} : userConfig,
        expiresInHours: parseInt(expiresInHours, 10),
        isPrivate,
        withAgent,
        subdomainSlug: slug,
        customDomainId:
          slug && effectiveDomainKey !== PLATFORM_DOMAIN_KEY ? effectiveDomainKey : undefined,
      });
      onOpenChange(false);
      onSuccess?.();
    } catch {
      // Error is handled by mutation state
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submit();
  };

  const updateConfigField = (key: string, value: string) => {
    setUserConfig((prev) => ({ ...prev, [key]: value }));
    if (errors[`config_${key}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`config_${key}`];
        return newErrors;
      });
    }
  };

  const clearError = (key: string) => {
    setErrors((prev) => {
      const nextErrors = { ...prev };
      delete nextErrors[key];
      return nextErrors;
    });
  };

  const footer = (
    <>
      <Button
        variant="outline"
        onPress={() => onOpenChange(false)}
        isDisabled={createMutation.isPending}
      >
        Cancel
      </Button>
      <Button
        onPress={() => void submit()}
        isDisabled={createMutation.isPending}
        iconLeft={<Cube size={16} />}
      >
        {createMutation.isPending ? 'Launching...' : 'Launch Instance'}
      </Button>
    </>
  );

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange} size="xl">
      <span />
      <Dialog
        eyebrow="New instance"
        title="Launch Environment Instance"
        description={`Create a new instance from the "${template.name}" template`}
        footer={footer}
      >
        {/* Form wrapper keeps Enter-to-submit (via the hidden submit button) and
            autoFocus; the visible action buttons live in the pinned Dialog footer. */}
        <form onSubmit={handleFormSubmit}>
          <Flex direction="column" gap="md">
            {/* Instance Name */}
            <TextField
              label="Instance Name"
              value={name}
              onChange={(v) => {
                setName(v);
                if (errors.name) {
                  clearError('name');
                }
              }}
              placeholder="e.g., my-test-env"
              errorMessage={errors.name}
              isRequired
              autoFocus
            />

            {/* Expiration */}
            <Box>
              <Select
                label="Expires After"
                items={EXPIRATION_OPTIONS}
                selectedKey={expiresInHours}
                onSelectionChange={(key) => setExpiresInHours(String(key))}
              />
              <Text size="xs" className="text-muted-foreground mt-1">
                Instance will automatically terminate after this time
              </Text>
            </Box>

            {/* Private Instance */}
            <Checkbox
              isSelected={isPrivate}
              onChange={setIsPrivate}
              label="Private instance"
              description="Not accessible from the internet. Use CLI tunneling to access."
            />

            <Checkbox
              isSelected={withAgent}
              onChange={setWithAgent}
              label="Attach AI Agent"
              description="Spins up the sandbox with an in-container Agent that can read and modify code, edit template artifacts, and respond to chat."
            />
            <Text size="xs" className="text-muted-foreground/70 -mt-2 ml-7">
              Startup time and performance improve a bit with no agent.
            </Text>

            {/* Custom URL (vanity slug + optional custom domain) */}
            {customUrlAvailable && (
              <CollapsibleSection
                header={
                  <Flex align="center" gap="sm">
                    <Globe size={16} className="text-muted-foreground" />
                    <Text weight="semibold" size="sm">
                      Custom URL
                    </Text>
                    <TagBadge name="Optional" color="gray" hideDot size="sm" />
                  </Flex>
                }
                bodyClassName="pb-1"
              >
                <Flex direction="column" gap="sm" className="pt-1">
                  <Flex gap="sm" align="start">
                    <Box className="flex-1">
                      <TextField
                        label="URL prefix"
                        value={customSlug}
                        onChange={(v) => {
                          setCustomSlug(v);
                          if (errors.customSlug) {
                            clearError('customSlug');
                          }
                        }}
                        placeholder="e.g., george-demo"
                        errorMessage={errors.customSlug}
                      />
                    </Box>
                    {domainOptions.length > 1 && (
                      <Box className="flex-1">
                        <Select
                          label="Domain"
                          items={domainOptions}
                          selectedKey={effectiveDomainKey}
                          onSelectionChange={(key) => setDomainKey(String(key))}
                        />
                      </Box>
                    )}
                  </Flex>
                  <Text size="xs" className="text-muted-foreground">
                    {hostnamePreview ? (
                      <>
                        Sandbox URL:{' '}
                        <span className="font-mono">https://{hostnamePreview}</span>
                      </>
                    ) : (
                      'Leave blank for an auto-generated URL.'
                    )}
                  </Text>
                </Flex>
              </CollapsibleSection>
            )}

            {/* Configuration Section */}
            {hasConfigSchema && (
              <CollapsibleSection
                icon={<Gear size={16} className="text-muted-foreground" />}
                label="Configuration"
                count={template.configSchema!.length}
                bodyClassName="pb-1"
              >
                <Flex direction="column" gap="md" className="pt-1">
                  <Checkbox
                    isSelected={useDefaults}
                    onChange={setUseDefaults}
                    label="Use default configuration"
                    description="Launch with the template's default settings"
                  />

                  {/* Show config fields when not using defaults */}
                  {!useDefaults && (
                    <Flex
                      direction="column"
                      gap="md"
                      className="pt-3 border-t border-border"
                    >
                      {[...template.configSchema!]
                        .sort((a, b) => {
                          // Required first, then alphabetically within each group
                          if (a.required !== b.required) return a.required ? -1 : 1;
                          return a.key.localeCompare(b.key);
                        })
                        .map((field) => (
                          <Box key={field.key}>
                            <Flex align="center" gap="xs" className="mb-1">
                              <Text size="sm" weight="medium" className="font-mono">
                                {field.key}
                              </Text>
                              {field.required && <Text className="text-destructive">*</Text>}
                              {field.sensitive && (
                                <Lock size={12} className="text-muted-foreground" />
                              )}
                            </Flex>
                            {field.sensitive ? (
                              <PasswordField
                                value={userConfig[field.key] || ''}
                                onChange={(v) => updateConfigField(field.key, v)}
                                description={field.description}
                                errorMessage={errors[`config_${field.key}`]}
                                placeholder="••••••••"
                              />
                            ) : (
                              <TextField
                                value={userConfig[field.key] || ''}
                                onChange={(v) => updateConfigField(field.key, v)}
                                description={field.description}
                                errorMessage={errors[`config_${field.key}`]}
                                placeholder={`Enter ${field.key}`}
                              />
                            )}
                          </Box>
                        ))}
                    </Flex>
                  )}
                </Flex>
              </CollapsibleSection>
            )}

            {/* Error message */}
            {createMutation.isError && (
              <Box className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                <Text size="sm" className="text-destructive">
                  {createMutation.error?.message || 'Failed to create instance'}
                </Text>
              </Box>
            )}
          </Flex>

          {/* Hidden submit keeps Enter-to-submit working; the visible button is in the footer. */}
          <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
        </form>
      </Dialog>
    </DialogTrigger>
  );
}
